/**
 * Typed API functions for the /api/v1/auth/* endpoints.
 *
 * All calls go through apiClient (axios singleton with Bearer interceptor + cookie support).
 * Architecture invariant: never inline axios.post directly in hooks/components.
 */

import { apiClient } from '@/lib/api/client';

// ─── Response types ────────────────────────────────────────────────────────

export interface RoleRef {
  code: string;
  name: string;
}

export interface UserMeResponse {
  id: string;
  email: string;
  full_name: string;
  phone: string | null;
  pan: string | null;
  address: string | null;
  type: string;
  roles: RoleRef[];
  permissions: string[];
  party_id: string | null;
  is_active: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
}

export interface AccessTokenResponse {
  access_token: string;
  token_type: 'bearer';
  expires_in_seconds: number;
}

// ─── Request types ─────────────────────────────────────────────────────────

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
}

export interface MessageResponse {
  message: string;
}

export interface ResetPasswordEmailResponse {
  user_id: string;
  email: string;
  temporary_password: string;
  email_sent: boolean;
}

// ─── API functions ─────────────────────────────────────────────────────────

/**
 * POST /api/v1/auth/login
 * Returns access token (refresh token arrives as an HTTP-only cookie).
 */
export async function login(credentials: LoginRequest): Promise<AccessTokenResponse> {
  const { data } = await apiClient.post<AccessTokenResponse>(
    '/auth/login',
    credentials
  );
  return data;
}

/**
 * POST /api/v1/auth/logout
 * Sends the HTTP-only refresh-token cookie (withCredentials is set on apiClient).
 * Backend expires the cookie via Set-Cookie Max-Age=0.
 */
export async function logout(): Promise<void> {
  // Backend declares `body: LogoutRequest` (empty model with extra="forbid")
  // for OpenAPI symmetry — send `{}` explicitly so FastAPI's body parser
  // doesn't 422 on missing JSON. The cookie itself carries the auth.
  await apiClient.post('/auth/logout', {});
}

/**
 * GET /api/v1/auth/me
 * Requires Authorization: Bearer <access_token> (attached by apiClient interceptor).
 */
export async function getCurrentUser(): Promise<UserMeResponse> {
  const { data } = await apiClient.get<UserMeResponse>('/auth/me');
  return data;
}

/**
 * POST /api/v1/auth/change-password
 * Requires Bearer token. Backend also expires the refresh cookie so other
 * sessions are revoked. The caller must re-authenticate after success.
 */
export async function changePassword(
  payload: ChangePasswordRequest
): Promise<MessageResponse> {
  const { data } = await apiClient.post<MessageResponse>('/auth/change-password', payload);
  return data;
}

/**
 * POST /api/v1/users/{user_id}/reset-password-email
 * Admin action. No request body. Returns the one-time temp password.
 * Gated by user.update permission.
 */
export async function resetPasswordEmail(userId: string): Promise<ResetPasswordEmailResponse> {
  const { data } = await apiClient.post<ResetPasswordEmailResponse>(
    `/users/${userId}/reset-password-email`
  );
  return data;
}
